import { Component, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { AddSelectedVendorUser, LoadVendorUsersForPackages, RemoveSelectedVendorUser } from '@completion/actions';
import {
  getSelectedSupplierCheckSheetAssignments,
  getVendorUsersForSelectedScope,
  getSelectedVendorUsersForScope,
  getVendors,
} from '@completion/selectors';
import { CheckSheetAssignmentProjection, User, Vendor } from '@completion/models';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-select-supplier',
  templateUrl: './select-supplier.component.html',
  styleUrls: ['./select-supplier.component.scss'],
  standalone: false,
})
export class SelectSupplierComponent implements OnDestroy {
  private destroy$ = new Subject<void>();
  selectedScope: CheckSheetAssignmentProjection[] = [];
  selectedVendorUsers: User[] = []; // available vendor users for selected scope
  chosenVendorUsers: User[] = []; // actually selected vendor users for the current scope
  vendors: Vendor[] = [];
  groupedSuppliers: Array<{ vendor: Vendor; users: User[] }> = [];

  constructor(private store: Store) {
    this.store
      .select(getSelectedSupplierCheckSheetAssignments)
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedCheckSheetAssignments) => {
        if (selectedCheckSheetAssignments && selectedCheckSheetAssignments.length > 0) {
          this.selectedScope = selectedCheckSheetAssignments;
          const packageIds = Array.from(
            new Set(
              this.selectedScope.map((scope) => scope.procurementPackageId).filter((id): id is number => typeof id === 'number' && id > 0),
            ),
          );

          if (packageIds.length > 0) {
            this.store.dispatch(new LoadVendorUsersForPackages(packageIds));
          }
        } else {
          this.selectedScope = [];
        }
      });

    this.store
      .select(getVendors)
      .pipe(takeUntil(this.destroy$))
      .subscribe((vendors) => {
        this.vendors = vendors ? [...vendors] : [];
        this.rebuildSupplierGroups();
      });

    // Available vendor users for the selected scope
    this.store
      .select(getVendorUsersForSelectedScope)
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedVendorUsers) => {
        if (selectedVendorUsers) {
          this.selectedVendorUsers = selectedVendorUsers;
        } else {
          this.selectedVendorUsers = [];
        }
        this.rebuildSupplierGroups();
      });

    // Chosen/selected vendor users for the selected scope
    this.store
      .select(getSelectedVendorUsersForScope)
      .pipe(takeUntil(this.destroy$))
      .subscribe((chosen) => {
        this.chosenVendorUsers = chosen ? [...chosen] : [];
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSupplierCheckboxChange(event: any, supplier: User): void {
    if (event.checked) {
      this.store.dispatch(new AddSelectedVendorUser(supplier));
    } else {
      this.store.dispatch(new RemoveSelectedVendorUser(supplier));
    }
  }

  isSelected(supplier: User): boolean {
    // Use userName as identity fallback if id is not available
    return this.chosenVendorUsers.some((u) => ((u as any).id ? (u as any).id === (supplier as any).id : u.userName === supplier.userName));
  }

  private rebuildSupplierGroups(): void {
    const scopeUserIds = new Set(this.selectedVendorUsers.map((user) => user.id));
    const hasScopeUsers = scopeUserIds.size > 0;

    this.groupedSuppliers = this.vendors
      .map((vendor) => {
        const users = (vendor.users || []).filter((user) => !hasScopeUsers || scopeUserIds.has(user.id));
        return {
          vendor,
          users,
        };
      })
      .filter((group) => group.users.length > 0)
      .sort((a, b) => (a.vendor.name || '').localeCompare(b.vendor.name || ''));
  }
}
